import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  fetchReservations,
  createReservation,
  updateReservation,
  assignAerolineToReservation,
  assignHotelToReservation,
  updateAerolineByReservation,
  updateHotelByReservation,
  deleteAerolineByReservation,
  deleteHotelByReservation,
  deleteReservation,
  assignRoomsToReservation,
} from "../../services/reservations";
import {
  formatAerolineReservationToDatabase,
  formatCotizationToDatabase,
  formatHotelReservationToDatabase,
  formatTours,
} from "../../util/util";
import { toast } from 'react-toastify';
import { getCurrentUser, getProfilesByIds } from "../../services/auth";


export const getReservations = createAsyncThunk(
  "reservations/fetchReservations",
  async (_, thunkAPI) => {
    try {
      await getCurrentUser();

      // Fetch all reservations (advisor filtering is client-side via UI)
      const res = await fetchReservations();

      if (res.error) {
        console.error('fetchReservations error:', res.error);
        return thunkAPI.rejectWithValue({ error: res.error.message || 'Error al cargar reservas' });
      }

      const reservations = res.data || [];

      const userIds = [...new Set(reservations.map(r => r.user_id).filter(Boolean))];

      if (userIds.length > 0) {
        const { data: profiles, error } = await getProfilesByIds(userIds);

        if (!error && profiles) {
          const profileMap = {};
          profiles.forEach(profile => {
            profileMap[profile.id] = profile;
          });

          return reservations.map(reservation => ({
            ...reservation,
            advisor: reservation.user_id && profileMap[reservation.user_id]
              ? `${profileMap[reservation.user_id].first_name || ''} ${profileMap[reservation.user_id].last_name || ''}`.trim() || profileMap[reservation.user_id].email
              : null
          }));
        }
      }

      return reservations.map(reservation => ({
        ...reservation,
        advisor: null
      }));
    } catch (err) {
      console.error('getReservations thunk error:', err);
      return thunkAPI.rejectWithValue({ error: err.message });
    }
  }
);

export const removeReservation = createAsyncThunk(
  "reservations/deleteReservation",
  async (id, thunkAPI) => {
    try {

      await toast.promise(
        deleteReservation(id).then((response) => {
          if (response.error) throw response.error;
          return response;
        }),
        {
          pending: {
            render() {
              return "Cargando"
            },
            icon: false,
          },
          success: {
            render({ data: responseData }) {
              return `Se eliminó correctamente 😏`
            },
            icon: "🚀",
          },
          error: {
            render({ data: errData }) {
              return errData?.message ? `Error: ${errData.message}` : `Ocurrió un error`
            }
          }
        }
      );


      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message });
    }
  }
);

export const editReservationStatus = createAsyncThunk("reservations/editReservationStatus",
  async ({ id, status }, thunkAPI) => {

    await toast.promise(
      updateReservation(id, { status: status }).then((response) => {
        if (response.error) throw response.error;
        return response;
      }),
      {
        pending: {
          render() {
            return "Cargando"
          },
          icon: false,
        },
        success: {
          render({ data: responseData }) {
            return `Se actualizó el estado de la reserva 😏`
          },
          icon: "🚀",
        },
        error: {
          render({ data: errData }) {
            return errData?.message ? `Error: ${errData.message}` : `Ocurrió un error`
          }
        }
      }
    );


    return { id, status };
  })

export const editReservation = createAsyncThunk(
  "reservations/editReservation",
  async ({ id, cotizationDetail, aerolinePrices, hotelPrices }, thunkAPI) => {
    try {

      const res = await toast.promise(
        updateReservation(
          id,
          formatCotizationToDatabase({ ...cotizationDetail, tours: formatTours(cotizationDetail.tours) })
        ).then((response) => {
          if (response.error) throw response.error;
          return response;
        }),
        {
          pending: {
            render() {
              return "Cargando"
            },
            icon: false,
          },
          success: {
            render({ data: responseData }) {
              return `Se actualizó correctamente 😏`
            },
            icon: "🚀",
          },
          error: {
            render({ data: errData }) {
              return errData?.message ? `Error: ${errData.message}` : `Ocurrió un error`
            }
          }
        }
      );


      /**
       * Creacion de cotizaciones y vuelos
       */

      const aerolinesToAddFormatted = aerolinePrices.toAdd.map((element) => {
        return formatAerolineReservationToDatabase({
          ...element,
          reservations_id: id,
          aerolines_id: element.id,
        });
      });

      const hotelsToAddFormatted = hotelPrices.toAdd.map((element) => {
        return formatHotelReservationToDatabase({
          ...element,
          reservations_id: id,
          hotels_id: element.id,
        });
      });

      const aerolinesAddedResponses = await assignAerolineToReservation(
        aerolinesToAddFormatted.map(({ id, ...rest }) => {
          return { ...rest };
        })
      );
      const hotelsAddedResponses = await assignHotelToReservation(
        hotelsToAddFormatted.map(({ id, ...rest }) => {
          return { ...rest };
        })
      );

      /**
       * Eliminacion de cotizaciones de vuelos y hoteles
       */

      await Promise.all(
        aerolinePrices.toDelete.map(({ table_id }) =>
          deleteAerolineByReservation(table_id)
        )
      );
      await Promise.all(
        hotelPrices.toDelete.map(({ table_id }) =>
          deleteHotelByReservation(table_id)
        )
      );

      /**
       *  Actualizacion de cotizaciones de vuelos y hoteles
       */

      const aerolinesReservationFormatted = aerolinePrices.toUpdate.map(
        (element) => formatAerolineReservationToDatabase(element)
      );

      const hotelsReservationFormatted = hotelPrices.toUpdate.map((element) =>
        formatHotelReservationToDatabase(element)
      );


      const aerolinesUpdatedResponses = await Promise.all(
        aerolinesReservationFormatted.map(({ id, ...aeroline }) =>
          updateAerolineByReservation(id, aeroline)
        )
      );
      const hotelUpdateResponses = await Promise.all(
        hotelsReservationFormatted.map(({ id, ...hotel }) =>
          updateHotelByReservation(id, hotel)
        )
      );

      return {
        cotizationDetail: res.data[0],
        hotelPrices: {
          toAdd: hotelsAddedResponses.data,
          toUpdate: hotelUpdateResponses.map((e) => e.data[0]),
          toDelete: hotelPrices.toDelete,
        },
        aerolinePrices: {
          toAdd: aerolinesAddedResponses.data,
          toUpdate: aerolinesUpdatedResponses.map((e) => e.data[0]),
          toDelete: aerolinePrices.toDelete,
        },
      };
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message });
    }
  }
);

export const createReservationThunk = createAsyncThunk(
  "reservations/createReservationThunk",
  async (_, thunkAPI) => {

    const state = thunkAPI.getState();
    const { cotizationDetail, aerolinePrices, accommodationCosts } = state.cotization;

    const { rooms, ...cotizationDetailBase } = cotizationDetail;

    if (true) return;

    try {
      const cotizationDetailFormattedToDatabase =
        formatCotizationToDatabase({ ...cotizationDetailBase, tours: formatTours(cotizationDetailBase.tours) });

      const responseReservation = await toast.promise(
        createReservation(cotizationDetailFormattedToDatabase).then((response) => {
          if (response.error) throw response.error;
          return response;
        }),
        {
          pending: {
            render() {
              return "Cargando"
            },
            icon: false,
          },
          success: {
            render({ data: responseData }) {
              return `Se creó la reserva 😏`
            },
            icon: "🚀",
          },
          error: {
            render({ data: errData }) {
              return errData?.message ? `Error: ${errData.message}` : `Ocurrió un error`
            }
          }
        }
      );

      const reservationCreated = responseReservation.data[0];
      const reservationId = reservationCreated.id;


      const roomsReservationFormatted = rooms.map((room, index) => ({
        ...room,
        room_number: index + 1,
        reservations_id: reservationId,
        children_ages: Object.values(room.children_ages)
      }));


      const aerolinesReservationFiltered = aerolinePrices.filter(
        (e) => e.price > 0
      );

      // const hotelsReservationFiltered = hotelPrices.filter((e) => e.price > 0);
      const aerolinesReservationFormatted = aerolinesReservationFiltered.map(
        (element) => {
          const { id, ...restFormatted } = formatAerolineReservationToDatabase({
            ...element,
            reservations_id: reservationCreated.id,
            aerolines_id: element.id,
          });

          return restFormatted;
        }
      );


      let hotelsReservationFormatted = []
      for (const key in accommodationCosts) {

        if (Object.hasOwnProperty.call(accommodationCosts, key)) {
          const { price, adultPrice, kidPrice, toddlerPrice } = accommodationCosts[key]
          hotelsReservationFormatted.push({
            reservations_id: reservationCreated.id,
            hotels_id: key,
            price,
            adult_price: adultPrice,
            kid_price: kidPrice,
            toddler_price: toddlerPrice
          })
        }
      }



      // const hotelsReservationFormatted = hotelPrices.map(
      //   (element) => {
      //     const { id, ...restFormatted } = formatHotelReservationToDatabase({
      //       ...element,
      //       reservations_id: reservationCreated.id,
      //       hotels_id: element.id,
      //     });

      //     return restFormatted;
      //   }
      // );


      const responseRooms = await assignRoomsToReservation(roomsReservationFormatted);
      const roomsCreated = responseRooms.data;

      const responseAerolinesReservations = await assignAerolineToReservation(
        aerolinesReservationFormatted
      );
      const aerolinesReservationCreated = responseAerolinesReservations.data;
      const responseHotelsReservations = await assignHotelToReservation(
        hotelsReservationFormatted
      );
      const hotelsReservationCreated = responseHotelsReservations.data;

      return {
        ...reservationCreated,
        reservations_rooms: roomsCreated,
        reservations_aerolines: aerolinesReservationCreated,
        reservations_hotels: hotelsReservationCreated,
      };
    } catch (err) {
      console.error('[createReservationThunk]', err.message);
      return thunkAPI.rejectWithValue({ error: err.message });
    }
  }
);
